import {HttpEvent, HttpHandlerFn, HttpRequest} from '@angular/common/http';
import {inject} from '@angular/core';
import {Dispatcher} from '@ngrx/signals/events';
import {EMPTY, Observable, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {AuthEvents} from '../ngrx/auth.events';
import {AuthStore} from '../ngrx/auth.store';
import {TokenRefreshQueue} from '../services/token-refresh-queue.service';
import {isAuthenticatedIdentity} from '../utils/identity.utils';

/**
 * Functional HTTP interceptor that detects 401 Unauthorized responses and silently
 * retries the request after token refresh succeeds.
 *
 * Behavior:
 * - On the first 401: starts a token refresh, waits for it to complete, then retries
 *   the original request once (browser sends refreshed cookie automatically).
 * - While refresh is in progress: additional 401s are queued and all retried together.
 * - If refresh fails: queued requests are silently abandoned (logout effect handles
 *   navigation). No error toast is shown for 401s that are successfully retried.
 * - Non-401 errors: propagate untouched.
 *
 * Register this interceptor AFTER `retryOnNetworkErrorInterceptor` so that transient
 * network failures (0, 502, 503, 504) are retried before 401 handling kicks in.
 *
 * @example
 * ```typescript
 * provideHttpClient(
 *   withInterceptors([retryOnNetworkErrorInterceptor, unauthorizedInterceptor])
 * )
 * ```
 */
export function unauthorizedInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const dispatcher = inject(Dispatcher);
  const authStore = inject(AuthStore);
  const queue = inject(TokenRefreshQueue);

  const identity = authStore.identity();

  return next(req).pipe(
    catchError(error => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // No active identity means this is an unauthenticated request (e.g. login).
      // There is nothing to refresh — propagate the error so the caller can handle it.
      if (!isAuthenticatedIdentity(identity)) {
        return throwError(() => error);
      }

      // Refresh already in progress → wait and retry once
      if (queue.isRefreshing) {
        return queue.waitForRefresh().pipe(switchMap(() => next(req)));
      }

      // Start refresh and wait
      queue.startRefresh();
      dispatcher.dispatch(AuthEvents.UnauthorizedError());

      return queue.waitForRefresh().pipe(
        switchMap(() => next(req)),
        catchError(() => {
          // Refresh failed → logout handles navigation, silently abandon request
          return EMPTY;
        })
      );
    })
  );
}
